import {
  Avatar,
  Box,
  Chip,
  Collapse,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Popover,
  Tooltip,
  useTheme,
} from '@mui/material';
import { ArrowDown2, ArrowUp2, Logout } from 'iconsax-react';
import { useDrawerContext } from 'context/DrawerContext';
import useRouter from 'hook/useRouter';
import navigationUtil from 'utils/navigation-util';
import React, { useState } from 'react';
import { matchPath } from 'react-router-dom';
import { useAuthContext } from 'context/AuthContext';
import { stringAvatar } from 'utils/validate-util';

const DrawerContent = () => {
  const { collapse } = useDrawerContext();
  /* State */
  const [anchorEl, setAnchorEl] = React.useState<HTMLDivElement | null>(null);

  const [navInCollapse, setNavInCollapse] = useState<number | null>(null);
  /* Hooks */

  // const scrollRef = document.getElementById('sidebar-scroll');
  const { location, navigate } = useRouter();
  const { logout, authState } = useAuthContext();
  const theme = useTheme();

  /* Methods */
  const handlePopoverOpen = (event: React.MouseEvent<HTMLDivElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  const isActivePage = (path: string) => {
    return Boolean(matchPath(path, location.pathname));
  };

  const open = Boolean(anchorEl);
  const id = open ? 'simple-popover' : undefined;

  return (
    <>
      <Avatar
        variant='square'
        src={'/images/mongkol_serey_logo_1000.png'}
        alt='logo'
        slotProps={{
          img: {
            sx: {
              objectFit: 'contain',
            },
          },
        }}
        sx={{
          mx: 'auto',
          width: collapse ? 48 : 100,
          height: collapse ? 48 : 100,
        }}
      />

      <Box
        sx={{
          height: '100%',
          position: 'relative',
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
        }}
      >
        <List
          sx={{
            p: 2,
          }}
        >
          {navigationUtil.map((nav, index) => {
            return nav.children.length ? (
              <React.Fragment key={index}>
                <Tooltip title={nav.title} key={index} arrow placement='right'>
                  <ListItemButton
                    onClick={(e) => {
                      setNavInCollapse((pre) => (pre !== index ? index : null));
                      collapse && handlePopoverOpen(e);
                    }}
                    sx={{ height: 48 }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: collapse ? 0 : 32,
                      }}
                    >
                      <nav.icon
                        size={18}
                        color={theme.palette.text.primary}
                        variant={isActivePage(nav.toUrl) ? 'Bold' : 'Linear'}
                      />
                    </ListItemIcon>

                    <ListItemText primary={nav.title} hidden={collapse} />
                    {!collapse &&
                      (navInCollapse === index ? (
                        <ArrowUp2 size='18' />
                      ) : (
                        <ArrowDown2 size='18' />
                      ))}
                  </ListItemButton>
                </Tooltip>
                <Collapse
                  in={!collapse ? navInCollapse === index : false}
                  timeout='auto'
                  unmountOnExit
                  sx={{
                    pl: 3,
                  }}
                >
                  <List
                    component='div'
                    disablePadding
                    sx={{ borderLeft: 1, borderColor: 'divider', pl: 1 }}
                  >
                    {nav.children.map((child, idx) => (
                      <ListItemButton
                        key={idx}
                        onClick={() => navigate(child.toUrl)}
                        selected={isActivePage(child.toUrl)}
                        dense
                      >
                        <ListItemText primary={child.title} />
                      </ListItemButton>
                    ))}
                  </List>
                </Collapse>
              </React.Fragment>
            ) : (
              <React.Fragment key={index}>
                <Tooltip title={nav.title} arrow placement='right'>
                  <ListItemButton
                    onClick={() => navigate(nav.toUrl)}
                    selected={isActivePage(nav.toUrl)}
                    sx={{ height: 48 }}
                  >
                    <ListItemIcon sx={{ minWidth: collapse ? 0 : 32 }}>
                      <nav.icon
                        size={18}
                        color={theme.palette.text.primary}
                        variant={isActivePage(nav.toUrl) ? 'Bold' : 'Linear'}
                      />
                    </ListItemIcon>
                    <ListItemText primary={nav.title} hidden={collapse} />
                  </ListItemButton>
                </Tooltip>
              </React.Fragment>
            );
          })}

          {/* Single Popover */}
          <Popover
            anchorOrigin={{
              vertical: 'center',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'center',
              horizontal: 'left',
            }}
            id={id}
            open={open}
            anchorEl={anchorEl}
            onClose={handlePopoverClose}
            sx={{
              zIndex: (theme) => theme.zIndex.drawer + 1,
              height: 300,
            }}
            elevation={1}
          >
            {navInCollapse !== null && (
              <List>
                {navigationUtil[navInCollapse].children.map((child, idx) => (
                  <ListItemButton
                    key={idx}
                    onClick={() => {
                      navigate(child.toUrl);
                      handlePopoverClose();
                    }}
                    selected={isActivePage(child.toUrl)}
                    dense
                  >
                    <ListItemText primary={child.title} />
                  </ListItemButton>
                ))}
              </List>
            )}
          </Popover>
        </List>
        <List
          sx={{
            bottom: 0,
            position: 'sticky',
            background: theme.palette.background.default,
            borderTop: 1,
            borderColor: 'divider',
          }}
        >
          <ListItem dense>
            <ListItemAvatar hidden={collapse}>
              <Avatar
                {...stringAvatar(
                  `${authState?.firstName} ${authState?.lastName}`,
                  {
                    height: 48,
                    width: 48,
                  }
                )}
              />
            </ListItemAvatar>
            <ListItemText
              primary={`${authState?.firstName} ${authState?.lastName}`}
              primaryTypographyProps={{
                variant: 'subtitle2',
              }}
              secondary={
                <Chip
                  label={authState?.isAdmin ? 'Admin' : 'Staff'}
                  color={authState?.isAdmin ? 'primary' : 'info'}
                  size='small'
                  sx={{
                    textTransform: 'capitalize',
                  }}
                  component={'span'}
                />
              }
              hidden={collapse}
            />
            <IconButton onClick={() => logout()}>
              <Logout size='24' />
            </IconButton>
          </ListItem>
        </List>
      </Box>
    </>
  );
};

export default DrawerContent;
